import { useEffect, useLayoutEffect, useSyncExternalStore } from 'react';
import { DENSITIES, type Density } from '@chapsi/contracts';
import { ACCENTS, TEXT_SIZES, THEME_STYLES, resolveTheme, themeCssVars, type Accent, type ColorScheme, type TextSize, type ThemeStyle } from '@chapsi/ui-tokens';

/**
 * Runtime theme (docs/adr/0016-themes-and-status.md). The person's appearance settings resolve to one
 * token set, written as CSS custom properties on <html>; components read only the --t-* variables.
 */
export type DarkMode = 'system' | 'light' | 'dark';
export interface Appearance {
  style: ThemeStyle;
  accent: Accent;
  darkMode: DarkMode;
  textSize: TextSize;
  /** Comfortable (default) or Compact (v4 Prompt 9): <html data-density>, read by the CSS density variables. */
  density: Density;
}

export const DEFAULT_APPEARANCE: Appearance = { style: 'calm', accent: 'violet', darkMode: 'system', textSize: 'default', density: 'comfortable' };

/**
 * The web and desktop apps have one style since v4 (docs/adr/0080): white headers, no purple bar, so Calm/Vibrant is retired.
 * `appearance.style` stays readable in contracts and saved settings (nothing is migrated or deleted; the phone still reads
 * it until its own restyle), but the web app always draws Calm and the Appearance screen no longer offers the choice.
 */
export const WEB_THEME_STYLE: ThemeStyle = 'calm';
/** The last look used in this browser, so the first paint is already right. Forgotten at sign-out (api/client.ts). */
export const APPEARANCE_CACHE_KEY = 'chapsi.appearance';
const CACHE_KEY = APPEARANCE_CACHE_KEY;
const DARK_QUERY = '(prefers-color-scheme: dark)';

function pick<T extends string>(options: readonly T[], value: unknown, fallback: T): T {
  return typeof value === 'string' && (options as readonly string[]).includes(value) ? (value as T) : fallback;
}

/** Appearance from effective settings (or anything else untrusted), falling back to the defaults. */
export function appearanceFrom(values: Record<string, unknown> | null | undefined): Appearance {
  const v = values ?? {};
  return {
    style: pick(THEME_STYLES, v['appearance.style'], DEFAULT_APPEARANCE.style),
    accent: pick(ACCENTS, v['appearance.accent'], DEFAULT_APPEARANCE.accent),
    darkMode: pick(['system', 'light', 'dark'] as const, v['appearance.darkMode'], DEFAULT_APPEARANCE.darkMode),
    textSize: pick(TEXT_SIZES, v['appearance.textSize'], DEFAULT_APPEARANCE.textSize),
    density: pick(DENSITIES, v['appearance.density'], DEFAULT_APPEARANCE.density),
  };
}

function systemDark(): boolean {
  try {
    return typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
}

export function schemeFor(darkMode: DarkMode, prefersDark = systemDark()): ColorScheme {
  return darkMode === 'dark' || (darkMode === 'system' && prefersDark) ? 'dark' : 'light';
}

let applied = '';

/** Writes the resolved theme onto <html>. Cheap to call repeatedly: unchanged input is skipped. */
export function applyTheme(a: Appearance, prefersDark = systemDark()): void {
  const scheme = schemeFor(a.darkMode, prefersDark);
  // A saved 'vibrant' is ignored on the web (WEB_THEME_STYLE).
  const style = WEB_THEME_STYLE;
  const signature = `${style}|${a.accent}|${scheme}|${a.textSize}|${a.density}`;
  if (signature === applied) return;
  applied = signature;
  const root = document.documentElement;
  const theme = resolveTheme({ style, accent: a.accent, scheme, textSize: a.textSize });
  for (const [k, v] of Object.entries(themeCssVars(theme))) root.style.setProperty(k, v);
  root.style.colorScheme = scheme;
  root.dataset.themeStyle = style;
  root.dataset.scheme = scheme;
  root.dataset.accent = a.accent;
  root.dataset.density = a.density;
}

/**
 * Before settings load: the last appearance used in this browser by the person signed in to it, else the defaults
 * (system dark honoured). Appearance is each person's own (docs/adr/0075): signing out forgets it, so the next
 * person to sign in on this browser never starts in someone else's theme or text size.
 */
export function bootTheme(): void {
  let cached: Record<string, unknown> | null = null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    cached = raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
  } catch {
    cached = null;
  }
  applyTheme(cached ? appearanceFrom({ 'appearance.style': cached.style, 'appearance.accent': cached.accent, 'appearance.darkMode': cached.darkMode, 'appearance.textSize': cached.textSize, 'appearance.density': cached.density }) : DEFAULT_APPEARANCE);
}

function subscribeScheme(cb: () => void): () => void {
  try {
    const mq = window.matchMedia(DARK_QUERY);
    mq.addEventListener('change', cb);
    return () => mq.removeEventListener('change', cb);
  } catch {
    return () => undefined;
  }
}

/** Whether the device prefers dark, kept live. */
export function usePrefersDark(): boolean {
  return useSyncExternalStore(subscribeScheme, systemDark, () => false);
}

/**
 * Keeps <html> in step with the person's appearance settings and the device's colour scheme. With no
 * settings yet (still loading) the boot theme stays.
 */
export function useThemeSync(values: Record<string, unknown> | null): void {
  const prefersDark = usePrefersDark();
  const a = values ? appearanceFrom(values) : null;
  const signature = a ? `${a.style}|${a.accent}|${a.darkMode}|${a.textSize}|${a.density}` : '';
  useLayoutEffect(() => {
    if (a) applyTheme(a, prefersDark);
  }, [signature, prefersDark]);
  useEffect(() => {
    if (!a) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(a));
    } catch {
      /* private mode */
    }
  }, [signature]);
}
